import "./StoreOnboardingAdmin.css";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, Card, Input, Select, SectionHeader, Table, FileText, NoticeBox, StatusDropdown } from "../../ui";
import { getStoreOnboardingAdminAPI } from "../../../utils/api/storeOnboarding";
import DetailView from "./components/DetailView/DetailView";
import { STATUS_OPTIONS, TEXT } from "./utils/onboardingAdmin.constants";
import { formatDate } from "../onboarding";

/*
  Historical onboarding records (Phase 4). The old quotation / payment / approval workflow is gone: payments are reviewed in Billing and a
  client's store information in the store panel below. This page only READS the old records (their data, uploaded documents, receipts and
  workflow history) and shows where each one's STORE stands now. Nothing here changes data.
*/

const STORE_STATUS_AR = { new: "جديد", inProgress: "قيد الإكمال", pendingReview: "بانتظار المراجعة", approved: "تمت الموافقة", rejected: "مرفوض للتعديل" };
const STORE_STATUS_EN = { new: "New", inProgress: "In progress", pendingReview: "Pending review", approved: "Approved", rejected: "Returned" };

export default function StoreOnboardingAdmin() {
  const { lang, handleTokenExpiration } = useContext(Context);
  const isArabic = lang === "ar";
  const t = TEXT[lang] || TEXT.ar;
  const detailRef = useRef(null);

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeStage, setActiveStage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "all", city: "" });

  const fetchData = async () => {
    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();
    setLoading(true);
    setLoadError("");
    try {
      const res = await getStoreOnboardingAdminAPI(token, 1, 100);
      setItems(res.items || []);
    } catch (error) {
      setLoadError(error.message || (isArabic ? "تعذر تحميل السجلات" : "Unable to load the records"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      items.filter((item) => {
        const search = filters.search.toLowerCase();
        const matchesSearch =
          !search ||
          item.store?.businessName?.toLowerCase().includes(search) ||
          item.store?.ownerName?.toLowerCase().includes(search) ||
          item.contact?.contactName?.toLowerCase().includes(search);
        const matchesStatus = filters.status === "all" || item.workFlow?.status === filters.status;
        const matchesCity = !filters.city || item.store?.city?.toLowerCase().includes(filters.city.toLowerCase());
        return matchesSearch && matchesStatus && matchesCity;
      }),
    [items, filters],
  );

  const statusOptions = useMemo(() => [{ value: "all", label: t.all }, ...STATUS_OPTIONS.map((status) => ({ value: status, label: t[status] }))], [t]);

  const columns = [
    { key: "store", label: "store", header: t.store, render: (row) => row.store?.businessName || "-" },
    { key: "city", label: "city", header: t.city, render: (row) => row.store?.city || "-" },
    { key: "stage", label: "stage", header: t.currentStage, render: (row) => row.workFlow?.currentStage || "-" },
    {
      key: "status",
      label: "status",
      header: t.status,
      render: (row) => <StatusDropdown editable={false} value={row?.workFlow?.status || "active"} getLabel={() => t[row.workFlow?.status] || row?.workFlow?.status} />,
    },
    {
      key: "storeOnboarding",
      label: "storeOnboarding",
      header: isArabic ? "حالة المتجر" : "Store onboarding",
      render: (row) =>
        row.storeOnboarding?.linked ? (
          <span dir="auto">
            {(isArabic ? STORE_STATUS_AR : STORE_STATUS_EN)[row.storeOnboarding.status] || row.storeOnboarding.status} · {row.storeOnboarding.percent}%
          </span>
        ) : (
          "-"
        ),
    },
    { key: "createdAt", label: "createdAt", header: t.created, render: (row) => formatDate(row.createdAt, lang) },
  ];

  return (
    <section className="onboarding-section store-onboarding-admin" dir={isArabic ? "rtl" : "ltr"}>
      <SectionHeader icon={FileText} title={t.title} description={t.description} />
      <NoticeBox variant="info" size="sm">
        {isArabic
          ? "سجلات تاريخية للقراءة فقط. تُراجع المدفوعات من صفحة الفواتير والاشتراكات، وتُراجع بيانات المتجر من لوحة المتجر داخل كل سجل."
          : "Historical records, read-only. Payments are reviewed in Billing; a client's store information in the store panel inside each record."}
      </NoticeBox>

      <Card variant="soft" padding="md" className="admin-onboarding-filters-card">
        <div className="admin-onboarding-filters">
          <Input value={filters.search} fullWidth={false} label={t.search} placeholder={t.searchPlaceholder} onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} />
          <Input value={filters.city} fullWidth={false} label={t.city} placeholder={t.cityPlaceholder} onChange={(event) => setFilters((prev) => ({ ...prev, city: event.target.value }))} />
          <Select label={t.status} value={filters.status} onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))} options={statusOptions} fullWidth={false} />
        </div>
      </Card>

      <Card className="admin-onboarding-table-card">
        {loading ? (
          <p role="status">{t.loading}</p>
        ) : loadError ? (
          <div role="alert">
            <NoticeBox variant="danger" title={loadError}>
              <Button size="sm" appearance="soft" onClick={fetchData}>
                {isArabic ? "إعادة المحاولة" : "Try again"}
              </Button>
            </NoticeBox>
          </div>
        ) : !filtered.length ? (
          <p className="admin-onboarding-empty-state">{t.noData}</p>
        ) : (
          <Table
            columns={columns}
            data={filtered}
            clickable={(row) => {
              setSelected(row);
              setActiveStage(row.workFlow?.currentStage || 1);
              setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
            }}
          />
        )}
      </Card>

      {selected && (
        <div ref={detailRef}>
          <DetailView data={selected} activeStage={activeStage} setActiveStage={setActiveStage} onClose={() => setSelected(null)} lang={lang} isArabic={isArabic} t={t} />
        </div>
      )}
    </section>
  );
}
